import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar } from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { CtaSection } from "../components/CtaSection";
import { usePageMeta } from "../hooks/usePageMeta";
import { CINEMATIC } from "../data/site";
import { fileUrl } from "../lib/files";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return "";
  }
};

const Insights = () => {
  usePageMeta(
    "Insights | Elin's Consultancy",
    "Plain‑English takes on HMO regulation, Stamp Duty Land Tax changes and leasehold reform from Elin's Consultancy."
  );
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/posts`)
      .then(({ data }) => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setError("Unable to load articles — please try again shortly."));
  }, []);

  return (
    <Layout>
      <PageHero
        eyebrow="Insights"
        lines={[
          "Property,",
          <>
            <em className="italic font-normal text-gold-soft">Thought Through</em>
          </>,
        ]}
        text="Short, plain‑English pieces on HMO licensing, Stamp Duty changes and the professional side of a property transaction — written for owners, investors and tenants, not other property professionals."
        image={CINEMATIC.archColumns}
        imageAlt="Classical fluted columns on a London period facade"
      />

      <section data-testid="insights-list" className="bg-cream">
        <div className="wrap-x py-20 sm:py-24">
          {error && (
            <p data-testid="insights-error" className="text-sm text-sage">
              {error}
            </p>
          )}
          {!error && posts === null && (
            <p className="text-sm text-sage">Loading articles…</p>
          )}
          {!error && posts && posts.length === 0 && (
            <p data-testid="insights-empty" className="text-sm text-sage">
              Articles are on the way — check back shortly.
            </p>
          )}
          {!error && posts && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {posts.map((p, i) => {
                const cover = p.cover_image ? fileUrl(p.cover_image) : null;
                return (
                  <Reveal key={p.id || p.slug} delay={i * 0.08}>
                    <article
                      data-testid={`insights-card-${i + 1}`}
                      className="group bg-white border border-forest/10 h-full flex flex-col transition-colors duration-500 hover:border-gold/60"
                    >
                      <Link
                        to={`/insights/${p.slug}`}
                        data-testid={`insights-card-link-${i + 1}`}
                        className="block"
                      >
                        {cover ? (
                          <div className="aspect-[16/10] overflow-hidden bg-cream-3">
                            <img
                              src={cover}
                              alt={p.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="aspect-[16/10] bg-forest/95 relative grain overflow-hidden">
                            <span className="absolute inset-0 flex items-center justify-center font-serif italic text-cream/80 text-4xl">
                              {p.category || "Insights"}
                            </span>
                          </div>
                        )}
                      </Link>
                      <div className="p-7 flex-1 flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-[10px] tracking-[0.26em] uppercase font-bold text-gold-dark">
                            {p.category || "Insights"}
                          </span>
                          <span className="w-6 h-px bg-forest/20" />
                          <span className="inline-flex items-center gap-1.5 text-[11px] text-sage">
                            <Calendar size={12} strokeWidth={1.6} />
                            {fmtDate(p.published_at || p.created_at)}
                          </span>
                        </div>
                        <h2 className="font-serif text-2xl font-medium text-forest leading-[1.2]">
                          <Link
                            to={`/insights/${p.slug}`}
                            className="hover:text-gold-dark transition-colors"
                          >
                            {p.title}
                          </Link>
                        </h2>
                        {p.excerpt && (
                          <p className="text-[13.5px] leading-[1.75] text-sage mt-4 flex-1">
                            {p.excerpt}
                          </p>
                        )}
                        <Link
                          to={`/insights/${p.slug}`}
                          data-testid={`insights-card-cta-${i + 1}`}
                          className="link-more mt-6 self-start"
                        >
                          Read Article <ArrowRight size={13} strokeWidth={2.2} />
                        </Link>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <CtaSection
        title={
          <>
            Have a question
            <br />
            <em className="italic font-normal text-gold-soft">we haven't covered?</em>
          </>
        }
        text="Property situations are rarely one‑size‑fits‑all. If a piece raised something specific to your purchase or portfolio, send us a note."
      />
    </Layout>
  );
};

export default Insights;
